import moment from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { ApiRelativeDirection, DateFieldFilterOperationType, RelativeDirection } from 'components/organisms/tasks/filters/date';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { unreachable } from '../../unreachable';
const RelativeFormatSeparator = '-';
const RelativeTimeDuration = 'd';
var RelativeDateValue = /*#__PURE__*/ function(RelativeDateValue) {
    RelativeDateValue["Today"] = "today";
    RelativeDateValue["Tomorrow"] = "tomorrow";
    RelativeDateValue["ThisWeek"] = "this_week";
    RelativeDateValue["LastWeek"] = "last_week";
    RelativeDateValue["NextWeek"] = "next_week";
    RelativeDateValue["ThisMonth"] = "this_month";
    RelativeDateValue["LastMonth"] = "last_month";
    RelativeDateValue["NextMonth"] = "next_month";
    RelativeDateValue["ThisQuarter"] = "this_quarter";
    RelativeDateValue["LastQuarter"] = "last_quarter";
    RelativeDateValue["ThisYear"] = "this_year";
    return RelativeDateValue;
}(RelativeDateValue || {});
const toStatic = (date)=>moment(date).format(DEFAULT_DATE_FORMAT);
const fromApiDirection = (direction)=>{
    switch(direction){
        case ApiRelativeDirection.After:
            return RelativeDirection.FromNow;
        case ApiRelativeDirection.Ago:
            return RelativeDirection.Ago;
        default:
            return unreachable(direction);
    }
};
const toApiDirection = (direction)=>{
    switch(direction){
        case RelativeDirection.Ago:
            return ApiRelativeDirection.Ago;
        case RelativeDirection.FromNow:
            return ApiRelativeDirection.After;
        default:
            return unreachable(direction);
    }
};
const toRelative = (days, direction)=>{
    return [
        JSON.stringify(days),
        RelativeTimeDuration,
        toApiDirection(direction)
    ].join(RelativeFormatSeparator);
};
const fromRelative = (value)=>{
    return value.split(RelativeFormatSeparator);
};
const isRelative = (value = '')=>{
    return value.includes(RelativeFormatSeparator + RelativeTimeDuration + RelativeFormatSeparator);
};
const isDirectionAgo = (value = '')=>value.includes(ApiRelativeDirection.Ago);
const toApiFormat = (filter)=>{
    switch(filter.type){
        case DateFieldFilterOperationType.IsEmpty:
            return {
                operation: Operation.IsEmpty,
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsNotEmpty:
            return {
                operation: Operation.IsNotEmpty,
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsBeforeToday:
            return {
                operation: Operation.LessThan,
                value: "today",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsToday:
            return {
                operation: Operation.Value,
                value: "today",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsNotToday:
            return {
                operation: Operation.IsNot,
                value: "today",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsTomorrow:
            return {
                operation: Operation.Value,
                value: "tomorrow",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsNotTomorrow:
            return {
                operation: Operation.IsNot,
                value: "tomorrow",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsThisWeek:
            return {
                operation: Operation.Value,
                value: "this_week",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsLastWeek:
            return {
                operation: Operation.Value,
                value: "last_week",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsNextWeek:
            return {
                operation: Operation.Value,
                value: "next_week",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsThisMonth:
            return {
                operation: Operation.Value,
                value: "this_month",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsLastMonth:
            return {
                operation: Operation.Value,
                value: "last_month",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsThisQuarter:
            return {
                operation: Operation.Value,
                value: "this_quarter",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsLastQuarter:
            return {
                operation: Operation.Value,
                value: "last_quarter",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsThisYear:
            return {
                operation: Operation.Value,
                value: "this_year",
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsAny:
            return {
                operation: Operation.Value,
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsMoreThan:
            return {
                operation: filter.direction === RelativeDirection.Ago ? Operation.LessThan : Operation.GreaterThan,
                value: toRelative(filter.days, filter.direction),
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsExactly:
            return {
                operation: Operation.Value,
                value: toRelative(filter.days, filter.direction),
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsLessThan:
            return {
                operation: filter.direction === RelativeDirection.Ago ? Operation.GreaterThan : Operation.LessThan,
                value: toRelative(filter.days, filter.direction),
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsAfter:
            return {
                operation: Operation.GreaterThan,
                value: filter.value == null ? 'null' : toStatic(filter.value),
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsOn:
            return {
                operation: Operation.Value,
                value: filter.value == null ? 'null' : toStatic(filter.value),
                sourceType: filter.field.sourceType
            };
        case DateFieldFilterOperationType.IsBefore:
            return {
                operation: Operation.LessThan,
                value: filter.value == null ? 'null' : toStatic(filter.value),
                sourceType: filter.field.sourceType
            };
        default:
            return unreachable(filter);
    }
};
const toType = (operation, value)=>{
    switch(operation){
        case Operation.HasValue:
        case Operation.IsNotEmpty:
            return DateFieldFilterOperationType.IsNotEmpty;
        case Operation.IsEmpty:
        case Operation.IsNull:
            return DateFieldFilterOperationType.IsEmpty;
        case Operation.LessThan:
            return value === "today" ? DateFieldFilterOperationType.IsBeforeToday : isRelative(value) ? isDirectionAgo(value) ? DateFieldFilterOperationType.IsMoreThan : DateFieldFilterOperationType.IsLessThan : DateFieldFilterOperationType.IsBefore;
        case Operation.GreaterThan:
            return isRelative(value) ? isDirectionAgo(value) ? DateFieldFilterOperationType.IsLessThan : DateFieldFilterOperationType.IsMoreThan : DateFieldFilterOperationType.IsAfter;
        case Operation.Value:
            switch(value){
                case "today":
                    return DateFieldFilterOperationType.IsToday;
                case "tomorrow":
                    return DateFieldFilterOperationType.IsTomorrow;
                case "this_week":
                    return DateFieldFilterOperationType.IsThisWeek;
                case "last_week":
                    return DateFieldFilterOperationType.IsLastWeek;
                case "next_week":
                    return DateFieldFilterOperationType.IsNextWeek;
                case "this_month":
                    return DateFieldFilterOperationType.IsThisMonth;
                case "last_month":
                    return DateFieldFilterOperationType.IsLastMonth;
                case "this_quarter":
                    return DateFieldFilterOperationType.IsThisQuarter;
                case "last_quarter":
                    return DateFieldFilterOperationType.IsLastQuarter;
                case "this_year":
                    return DateFieldFilterOperationType.IsThisYear;
                default:
                    return isRelative(value) ? DateFieldFilterOperationType.IsExactly : DateFieldFilterOperationType.IsOn;
            }
        case Operation.IsNot:
        case Operation.OneOf:
        case Operation.Contains:
        case Operation.NoneOf:
        default:
            return unreachable(operation);
    }
};
const parse = (filter, field)=>{
    var _filter_value;
    const type = toType(filter.operation, filter.value);
    const value = (_filter_value = filter.value) !== null && _filter_value !== void 0 ? _filter_value : '';
    switch(type){
        case DateFieldFilterOperationType.IsEmpty:
        case DateFieldFilterOperationType.IsNotEmpty:
        case DateFieldFilterOperationType.IsBeforeToday:
        case DateFieldFilterOperationType.IsToday:
        case DateFieldFilterOperationType.IsNotToday:
        case DateFieldFilterOperationType.IsTomorrow:
        case DateFieldFilterOperationType.IsNotTomorrow:
        case DateFieldFilterOperationType.IsThisWeek:
        case DateFieldFilterOperationType.IsLastWeek:
        case DateFieldFilterOperationType.IsNextWeek:
        case DateFieldFilterOperationType.IsThisMonth:
        case DateFieldFilterOperationType.IsLastMonth:
        case DateFieldFilterOperationType.IsThisQuarter:
        case DateFieldFilterOperationType.IsLastQuarter:
        case DateFieldFilterOperationType.IsThisYear:
        case DateFieldFilterOperationType.IsAny:
            return {
                field,
                type
            };
        case DateFieldFilterOperationType.IsMoreThan:
        case DateFieldFilterOperationType.IsExactly:
        case DateFieldFilterOperationType.IsLessThan:
            {
                const [days, _, direction] = fromRelative(value);
                return {
                    field,
                    type,
                    days: JSON.parse(days),
                    direction: fromApiDirection(direction)
                };
            }
        case DateFieldFilterOperationType.IsAfter:
        case DateFieldFilterOperationType.IsOn:
        case DateFieldFilterOperationType.IsBefore:
            {
                const moment_value = JSON.parse(`"${value}"`);
                return {
                    field,
                    type,
                    value: moment_value == null ? undefined : moment(moment_value)
                };
            }
        default:
            return unreachable(type);
    }
};
export const DateFilter = {
    toApiFormat,
    parse
};
